import React from 'react'
import type { BetSlipResponse, FobBetReceiptResponse } from '@mobi/betslip/types'
import { Money } from '@core/Components/Text/Money'
import { NoticeBox } from '@core/Components/NoticeBox'
import {
  ReceiptCampaignTextStyled,
  ReceiptListItemStyled,
  ReceiptListItemTextStyled,
  ReceiptListRowStyled,
  ReceiptListStyled,
  RecieptButtonsContainerStyled,
  RecieptContainerStyled,
} from '../Common/Receipt.styles'
import { ShareButtonMulti } from './Components/ShareButtonMulti'
import { isFobBetReceiptResponse } from '@mobi/betslip/helpers/typeGuards'

export type BetslipReceiptProps = {
  responses: BetSlipResponse[]
}

export const BetSlipReceipt: React.FC<BetslipReceiptProps> = ({ responses }) => {
  const successfulBets = responses.filter(bet => bet.success)
  const unsuccessfulBets = responses.filter(bet => !bet.success)
  const allSuccess = successfulBets.length > 0 && unsuccessfulBets.length == 0

  const pluralization = successfulBets.length > 1 ? 'bets have been' : 'bet has been'

  const receiptState =
    responses.length == 1 && successfulBets.length == 1
      ? (successfulBets[0].receipt as FobBetReceiptResponse)
      : undefined

  const isMultiBet = responses[0].legs && responses[0].legs.length > 1
  const shouldRenderMultiShare = isMultiBet && isFobBetReceiptResponse(receiptState)

  return (
    <RecieptContainerStyled>
      <NoticeBox
        noticeBoxType={allSuccess ? 'Success' : 'Warning'}
        title={`Your ${pluralization} placed.`}
        paddingOverride='0'
      >
        <BetslipReceiptDetails responses={responses} />
      </NoticeBox>

      <RecieptButtonsContainerStyled>
        {shouldRenderMultiShare && receiptState && <ShareButtonMulti receipt={receiptState} />}
      </RecieptButtonsContainerStyled>
    </RecieptContainerStyled>
  )
}

const BetslipReceiptDetails: React.FC<BetslipReceiptProps> = ({ responses }) => {
  const successfulBets = responses.filter(bet => bet.success)
  const unsuccessfulBets = responses.filter(bet => !bet.success)

  const receipt = successfulBets[0]?.receipt
  const betCost = receipt && 'betCost' in receipt ? receipt.betCost : 0
  const bonusBet = receipt && 'bonusBet' in receipt ? receipt.bonusBet : 0
  const amountPaid = receipt && 'amountPaid' in receipt ? receipt.amountPaid : 0
  const projectedWinPayout =
    receipt && 'projectedWinPayout' in receipt ? receipt.projectedWinPayout : null

  const ticketNumber =
    responses.length == 1 && successfulBets.length == 1
      ? successfulBets[0].receipt?.ticketNumber
      : false

  const isMultiBet = responses[0].legs && responses[0].legs.length > 1

  return (
    <ReceiptListStyled>
      {isMultiBet ? null : (
        <ReceiptListRowStyled data-tid-receipt-successful-bets=''>
          <ReceiptListItemStyled>Bets Placed</ReceiptListItemStyled>
          <ReceiptListItemTextStyled>{successfulBets.length}</ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      )}

      {unsuccessfulBets.length > 0 ? (
        <ReceiptListRowStyled data-tid-receipt-unsuccessful-bets=''>
          <ReceiptListItemStyled>Failed Bets</ReceiptListItemStyled>
          <ReceiptListItemTextStyled>{unsuccessfulBets.length}</ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      ) : null}

      {isMultiBet ? null : (
        <ReceiptListRowStyled data-tid-receipt-betcost=''>
          <ReceiptListItemStyled>Bet Cost</ReceiptListItemStyled>
          <ReceiptListItemTextStyled>
            <Money amount={betCost} />
          </ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      )}

      {bonusBet > 0 ? (
        <ReceiptListRowStyled data-tid-receipt-bonusbet=''>
          <ReceiptListItemStyled>
            <ReceiptCampaignTextStyled isBonusBet={false}>Bonus Cash</ReceiptCampaignTextStyled>
          </ReceiptListItemStyled>
          <ReceiptListItemTextStyled>
            <ReceiptCampaignTextStyled isBonusBet={false}>
              <Money amount={-bonusBet} />
            </ReceiptCampaignTextStyled>
          </ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      ) : null}

      <ReceiptListRowStyled data-tid-receipt-amountpaid=''>
        <ReceiptListItemStyled>Amount Paid</ReceiptListItemStyled>
        <ReceiptListItemTextStyled>
          <Money amount={amountPaid} />
        </ReceiptListItemTextStyled>
      </ReceiptListRowStyled>

      {projectedWinPayout ? (
        <ReceiptListRowStyled data-tid-receipt-amountpaid=''>
          <ReceiptListItemStyled>Potential Payout</ReceiptListItemStyled>
          <ReceiptListItemTextStyled>
            <Money amount={projectedWinPayout} />
          </ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      ) : null}

      {ticketNumber ? (
        <ReceiptListRowStyled data-tid-receipt-ticket=''>
          <ReceiptListItemStyled>Ticket</ReceiptListItemStyled>
          <ReceiptListItemTextStyled>
            {successfulBets[0].receipt?.ticketNumber}
          </ReceiptListItemTextStyled>
        </ReceiptListRowStyled>
      ) : null}

      {!ticketNumber ? (
        <ReceiptListRowStyled>Please check Pending Bets for details</ReceiptListRowStyled>
      ) : null}
    </ReceiptListStyled>
  )
}
